import { CommonModule } from '@angular/common';
import { NgModule, Optional, SkipSelf } from '@angular/core';
import { NgbDateParserFormatter, NgbDatepickerI18n } from '@ng-bootstrap/ng-bootstrap';

import { AuthInteractor } from './interactors/auth.interactor';
import { ChecklistInteractor } from './interactors/checklist.interactor';
import { ConsentFormInteractor } from './interactors/consent-form.interactor';
import { DeviceInteractor } from './interactors/device.interactor';
import { EvaluationInteractor } from './interactors/evaluation.interactor';
import { MeasGroupInteractor } from './interactors/meas-group.interactor';
import { MgmtDeptInteractor } from './interactors/mgmt-dept.interactor';
import { ModelInteractor } from './interactors/model.interactor';
import { OrganizationInteractor } from './interactors/organization.interactor';
import { ProgressInteractor } from './interactors/progress.interactor';
import { ProjectInteractor } from './interactors/project.interactor';
import { ReportInteractor } from './interactors/report.interactor';
import { ResultInteractor } from './interactors/result.interactor';
import { ThinkletInteractor } from './interactors/thinklet.interactor';
import { ToastInteractor } from './interactors/toast.interactor';
import { UserInteractor } from './interactors/user.interactor';
import { WebSocketInteractor } from './interactors/web-socket.interactor';
import { httpInterceptorProviders } from './interceptors';
import { AuthService } from './services/auth.service';
import { ChecklistService } from './services/checklist.service';
import { CloudStorageService } from './services/cloud-storage.service';
import { ConsentFormService } from './services/consent-form.service';
import { DateParserFormatterService } from './services/date-parser-formatter.service';
import { DatepickerI18nService } from './services/datepicker-i18n.service';
import { DeviceService } from './services/device.service';
import { EvaluationService } from './services/evaluation.service';
import { MeasGroupService } from './services/meas-group.service';
import { MgmtDeptService } from './services/mgmt-dept.service';
import { ModelService } from './services/model.service';
import { OrganizationService } from './services/organization.service';
import { ProjectService } from './services/project.service';
import { ReportService } from './services/report.service';
import { ResultService } from './services/result.service';
import { ThinkletService } from './services/thinklet.service';
import { UnitService } from './services/unit.service';
import { UserService } from './services/user.service';
import { AuthGateway } from './usecases/auth.gateway';
import { AuthUsecase } from './usecases/auth.usecase';
import { ChecklistGateway } from './usecases/checklist.gateway';
import { ChecklistUsecase } from './usecases/checklist.usecase';
import { CloudStorageGateway } from './usecases/cloud-storage.gateway';
import { ConsentFormGateway } from './usecases/consent-form.gateway';
import { ConsentFormUsecase } from './usecases/consent-form.usecase';
import { DeviceGateway } from './usecases/device.gateway';
import { DeviceUsecase } from './usecases/device.usecase';
import { EvaluationGateway } from './usecases/evaluation.gateway';
import { EvaluationUsecase } from './usecases/evaluation.usecase';
import { MeasGroupGateway } from './usecases/meas-group.gateway';
import { MeasGroupUsecase } from './usecases/meas-group.usecase';
import { MgmtDeptGateway } from './usecases/mgmt-dept.gateway';
import { MgmtDeptUsecase } from './usecases/mgmt-dept.usecase';
import { ModelGateway } from './usecases/model.gateway';
import { ModelUsecase } from './usecases/model.usecase';
import { OrganizationGateway } from './usecases/organization.gateway';
import { OrganizationUsecase } from './usecases/organization.usecase';
import { ProgressUsecase } from './usecases/progress.usecase';
import { ProjectGateway } from './usecases/project.gateway';
import { ProjectUsecase } from './usecases/project.usecase';
import { ReportGateway } from './usecases/report.gateway';
import { ReportUsecase } from './usecases/report.usecase';
import { ResultGateway } from './usecases/result.gateway';
import { ResultUsecase } from './usecases/result.usecase';
import { ThinkletGateway } from './usecases/thinklet.gateway';
import { ThinkletUsecase } from './usecases/thinklet.usecase';
import { ToastUsecase } from './usecases/toast.usecase';
import { UnitGateway } from './usecases/unit.gateway';
import { UserGateway } from './usecases/user.gateway';
import { UserUsecase } from './usecases/user.usecase';
import { WebSocketUsecase } from './usecases/web-socket.usecase';

@NgModule({
  providers: [
    httpInterceptorProviders,
    { provide: NgbDateParserFormatter, useClass: DateParserFormatterService },
    { provide: NgbDatepickerI18n, useClass: DatepickerI18nService },
    { provide: AuthUsecase, useClass: AuthInteractor },
    { provide: ChecklistUsecase, useClass: ChecklistInteractor },
    { provide: ConsentFormUsecase, useClass: ConsentFormInteractor },
    { provide: DeviceUsecase, useClass: DeviceInteractor },
    { provide: MeasGroupUsecase, useClass: MeasGroupInteractor },
    { provide: MgmtDeptUsecase, useClass: MgmtDeptInteractor },
    { provide: ModelUsecase, useClass: ModelInteractor },
    { provide: OrganizationUsecase, useClass: OrganizationInteractor },
    { provide: ProgressUsecase, useClass: ProgressInteractor },
    { provide: ProjectUsecase, useClass: ProjectInteractor },
    { provide: ReportUsecase, useClass: ReportInteractor },
    { provide: ResultUsecase, useClass: ResultInteractor },
    { provide: ThinkletUsecase, useClass: ThinkletInteractor },
    { provide: ToastUsecase, useClass: ToastInteractor },
    { provide: UserUsecase, useClass: UserInteractor },
    { provide: WebSocketUsecase, useClass: WebSocketInteractor },
    { provide: AuthGateway, useClass: AuthService },
    { provide: ChecklistGateway, useClass: ChecklistService },
    { provide: ConsentFormGateway, useClass: ConsentFormService },
    { provide: CloudStorageGateway, useClass: CloudStorageService },
    { provide: DeviceGateway, useClass: DeviceService },
    { provide: MeasGroupGateway, useClass: MeasGroupService },
    { provide: MgmtDeptGateway, useClass: MgmtDeptService },
    { provide: ModelGateway, useClass: ModelService },
    { provide: OrganizationGateway, useClass: OrganizationService },
    { provide: ProjectGateway, useClass: ProjectService },
    { provide: ReportGateway, useClass: ReportService },
    { provide: ResultGateway, useClass: ResultService },
    { provide: ThinkletGateway, useClass: ThinkletService },
    { provide: UserGateway, useClass: UserService },
    { provide: UnitGateway, useClass: UnitService },
    { provide: EvaluationGateway, useClass: EvaluationService },
    { provide: EvaluationUsecase, useClass: EvaluationInteractor },
  ],
  imports: [CommonModule],
})
export class CoreModule {
  constructor(@Optional() @SkipSelf() parentModule: CoreModule) {
    if (parentModule) {
      throw new Error('CoreModule is already loaded. Import it in the AppModule Only.');
    }
  }
}
